import { Component } from '@theme/component';

/**
 * Collapses text to a set number of lines and reveals the rest on click.
 * The button stays hidden when the text already fits.
 *
 * @typedef {Object} TextClampRefs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<TextClampRefs>}
 */
class TextClampComponent extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {boolean} */
  #measuring = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#sync();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#sync());
      this.#resizeObserver.observe(this);
    }

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#sync();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();
    this.#expanded = !this.#expanded;
    this.#apply(true);
  }

  /**
   * Show the toggle only when the clamped text is shorter than the full text.
   */
  #sync() {
    if (this.#measuring) return;

    const { content, toggle } = this.refs;
    if (!content || !toggle) return;

    if (content.getBoundingClientRect().width === 0) return;

    this.#measuring = true;
    const overflows = this.#contentOverflows();
    this.#measuring = false;

    if (!overflows) this.#expanded = false;

    toggle.hidden = !overflows;
    this.#apply(overflows);
  }

  /**
   * @param {boolean} overflows
   */
  #apply(overflows) {
    const { content, toggle } = this.refs;
    if (!content || !toggle) return;

    const clamp = overflows && !this.#expanded;
    if (clamp) {
      content.setAttribute('data-clamped', '');
    } else {
      content.removeAttribute('data-clamped');
    }

    const expanded = this.#expanded ? 'true' : 'false';
    this.dataset.expanded = expanded;
    toggle.setAttribute('aria-expanded', expanded);
  }

  /**
   * Compare a hidden copy so measuring does not flash the full text.
   * @returns {boolean}
   */
  #contentOverflows() {
    const { content } = this.refs;
    const width = content.getBoundingClientRect().width;
    if (width === 0) return false;

    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('id');
    clone.setAttribute('data-clamped', '');
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.width = `${width}px`;
    clone.style.maxWidth = 'none';
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    content.insertAdjacentElement('afterend', clone);

    const clampedHeight = clone.offsetHeight;
    clone.removeAttribute('data-clamped');
    const fullHeight = clone.offsetHeight;
    clone.remove();

    return fullHeight > clampedHeight + 1;
  }
}

if (!customElements.get('text-clamp-component')) {
  customElements.define('text-clamp-component', TextClampComponent);
}
